import type {
    PracticeBlockName,
    PracticeDetail,
    PracticeDetailFieldKey,
    PracticeDetailUpdate
} from '../../../../services/practiceDetail';
import { PRACTICE_RISK_ICONS, PRACTICE_RISK_STATUS_LABELS } from '../../../shared/practiceRisk.ts';
import type { PracticeFieldToneOption } from './ToneDropdownField';

// Single source of truth for how the flat form model renders and maps onto the block-structured
// GET/PATCH/POST contract (services/practiceDetail.ts); unlisted wire keys are never sent.

export type PracticeFieldType = 'text' | 'tel' | 'email' | 'longtext' | 'select' | 'number';

// Where a field lives on the wire - 'top' for `name` (the only editable
// field outside the five named blocks), otherwise the block it's nested
// under for both GET (reading) and PATCH (writing).
export type PracticeFieldBlock = 'top' | PracticeBlockName;

export interface PracticeFieldDef {
    key: PracticeDetailFieldKey;
    label: string;
    type: PracticeFieldType;
    /**
     * Grid columns spanned in the 12-column field grid (12 = full row) - see
     * D2Field in the design source. Field order within a group's `fields`
     * array IS the grid order (no separate layout list to keep in sync), so
     * a row's spans are expected to sum to 12 wherever the mock implies a
     * clean row break.
     */
    span: number;
    block: PracticeFieldBlock;
    /** Renders the value in a monospace font (external ids). */
    mono?: boolean;
    /** Shown under a longtext field. */
    helperText?: string;
    /**
     * NOT NULL on the backend - clearing it to empty must fail client-side
     * validation instead of sending null, which the server rejects.
     */
    required?: boolean;
    /**
     * Groups this field under a labelled subsection within its card (Account
     * only, for now). Consecutive fields sharing the same label are grouped
     * together - see groupFieldsBySubsection - so this array's field order
     * IS the subsection order; there's no separate ordering list to keep
     * in sync.
     */
    subsectionLabel?: string;
    /** Overrides a select option's displayed label without changing its wire
     * value (PRACTICE_FIELD_OPTIONS stays the source of raw values). */
    optionLabels?: Record<string, string>;
    /** HTML maxlength for a longtext field. */
    maxLength?: number;
    /** Placeholder text for a text/longtext field's empty input. */
    placeholder?: string;
    /** Render kind override - 'toneDropdown' swaps the native `<select>` for
     * ToneDropdownField's tone-tinted chip trigger + popover. */
    kind?: 'toneDropdown';
    /** Per-option styling for a 'toneDropdown' field. See RISK_STATUS_TONE_OPTIONS below. */
    toneOptions?: PracticeFieldToneOption[];
    /** Dims this field while another field holds a specific value (see
     * DetailFieldGrid) - purely visual, never touches the typed value. */
    deemphasizeWhenFieldEquals?: { field: string; value: string };
}

export type PracticeFieldGroupId = 'practice' | 'doctor' | 'account' | 'risk';

// Matches the backend's PATCH validation for risk.riskNote - enforced both
// here (maxLength on the field def, read by DetailField's textarea AND by
// maxLengthFieldError below) so a too-long note is caught before the round
// trip, not just truncated silently by the textarea.
export const PRACTICE_RISK_NOTE_MAX_LENGTH = 2000;

export interface PracticeFieldGroup {
    id: PracticeFieldGroupId;
    label: string;
    fields: PracticeFieldDef[];
}

// Tones for the editable risk-status dropdown (see ToneDropdownField) - the
// trigger wears the current option's full color, the popover lists every
// option in its own muted tone, mirroring the work-queue priority selector's
// "the whole control wears the tone" language exactly (see
// work-queue/priority/PrioritySelector.tsx's PRIORITY_BUTTON_TONES: gray for
// normal, amber for the middle tier, red for the top tier). Only this one
// editable control uses these tones, so they live here rather than in the
// shared practiceRisk.ts display classes (PRACTICE_RISK_TEXT_CLASSES/
// PRACTICE_RISK_PILL_CLASSES), which back read-only surfaces instead.
export const RISK_STATUS_TONE_OPTIONS: PracticeFieldToneOption[] = [
    {
        value: 'normal',
        label: PRACTICE_RISK_STATUS_LABELS.normal,
        triggerClasses: 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
        menuLabelClasses: 'text-gray-700',
        neutral: true
    },
    {
        value: 'warning',
        label: PRACTICE_RISK_STATUS_LABELS.warning,
        icon: PRACTICE_RISK_ICONS.warning,
        triggerClasses: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
        triggerIconClasses: 'text-amber-600',
        menuLabelClasses: 'text-amber-700',
        menuIconClasses: 'text-amber-600'
    },
    {
        value: 'at_risk',
        label: PRACTICE_RISK_STATUS_LABELS.at_risk,
        icon: PRACTICE_RISK_ICONS.at_risk,
        triggerClasses: 'border-red-600 bg-red-600 text-white hover:bg-red-700',
        triggerIconClasses: 'text-white',
        menuLabelClasses: 'text-red-700',
        menuIconClasses: 'text-red-600'
    }
];

export const EQUIPMENT_SUBSECTION_LABEL = 'Equipment';

// Practice card row layout (spans sum to 12 per row, per the design source):
//   name(5) phone(3) email(4)
//   street(6) suite(2) secondaryEmail(4)
//   city(4) state(3) zip(2) country(3)
const PRACTICE_FIELD_GROUPS: PracticeFieldGroup[] = [
    {
        id: 'practice',
        label: 'Practice',
        fields: [
            {
                key: 'name',
                label: 'Practice name',
                type: 'text',
                span: 5,
                block: 'top',
                required: true
            },
            { key: 'phone', label: 'Phone', type: 'tel', span: 3, block: 'contact' },
            { key: 'email', label: 'Email', type: 'email', span: 4, block: 'contact' },
            { key: 'address', label: 'Street address', type: 'text', span: 9, block: 'address' },
            {
                key: 'addressDetails',
                label: 'Suite / unit',
                type: 'text',
                span: 3,
                block: 'address'
            },
            { key: 'city', label: 'City', type: 'text', span: 4, block: 'address' },
            { key: 'state', label: 'State', type: 'select', span: 3, block: 'address' },
            { key: 'zip', label: 'ZIP', type: 'text', span: 2, block: 'address', mono: true },
            { key: 'country', label: 'Country', type: 'select', span: 3, block: 'address' }
        ]
    },
    {
        id: 'doctor',
        label: 'Doctor',
        fields: [
            {
                key: 'drFirstName',
                label: 'First name',
                type: 'text',
                span: 4,
                block: 'doctor',
                required: true
            },
            {
                key: 'drLastName',
                label: 'Last name',
                type: 'text',
                span: 4,
                block: 'doctor',
                required: true
            },
            { key: 'drEmail', label: 'Email', type: 'email', span: 4, block: 'doctor' },
            {
                key: 'designPreferences',
                label: 'Practice wide preferences',
                type: 'longtext',
                span: 12,
                block: 'doctor',
                helperText: 'Shown to the design team on every case for this doctor.'
            }
        ]
    },
    {
        id: 'account',
        label: 'Account',
        fields: [
            // Classification
            {
                key: 'accountType',
                label: 'Account type',
                type: 'select',
                span: 4,
                block: 'account',
                subsectionLabel: 'Classification'
            },
            {
                key: 'version',
                label: 'Version',
                type: 'select',
                span: 4,
                block: 'account',
                subsectionLabel: 'Classification'
            },
            // Dental Monitoring - numeric ID strings on the wire, not JS
            // numbers (external DM system IDs) - rendered as opaque mono
            // text, like billId.
            {
                key: 'dmPracticeId',
                label: 'DM practice ID',
                type: 'text',
                span: 4,
                block: 'account',
                mono: true,
                subsectionLabel: 'Dental Monitoring'
            },
            {
                key: 'dmDoctorId',
                label: 'DM doctor ID',
                type: 'text',
                span: 4,
                block: 'account',
                mono: true,
                subsectionLabel: 'Dental Monitoring'
            },
            // Billing - billId/billingEmail/ach sum to a clean 12 across the
            // row (4 + 5 + 3); daysNet/installments (4 + 3) intentionally
            // don't fill the rest of their row - see each field's own span
            // guidance in the task spec (ids/selects 3-4, emails 4-5).
            {
                key: 'billId',
                label: 'Bill.com ID',
                type: 'text',
                span: 4,
                block: 'account',
                mono: true,
                subsectionLabel: 'Billing'
            },
            {
                key: 'billingEmail',
                label: 'Billing email',
                type: 'email',
                span: 5,
                block: 'account',
                subsectionLabel: 'Billing'
            },
            {
                key: 'ach',
                label: 'ACH',
                type: 'select',
                span: 3,
                block: 'account',
                subsectionLabel: 'Billing'
            },
            // Looks numeric but is a Monday *status* column with compound
            // preset labels (e.g. "30,60,90") - confirmed live. An enum
            // field like the other five, not free text or a real number.
            {
                key: 'daysNet',
                label: 'Payment terms (days net)',
                type: 'select',
                span: 4,
                block: 'account',
                subsectionLabel: 'Billing'
            },
            {
                key: 'installments',
                label: 'Installments',
                type: 'number',
                span: 3,
                block: 'account',
                subsectionLabel: 'Billing'
            },
            // Manufacturer - wider (6 + 6 = a clean 12) since account handles
            // read as free text, not short codes like the ids above.
            {
                key: 'sparkAcct',
                label: 'Spark account',
                type: 'text',
                span: 6,
                block: 'account',
                subsectionLabel: 'Manufacturer'
            },
            {
                key: 'invisalignAcct',
                label: 'Invisalign account',
                type: 'text',
                span: 6,
                block: 'account',
                subsectionLabel: 'Manufacturer'
            },
            // Equipment writes to the `equipment` wire block; Details widens Scanner (see below).
            {
                key: 'scanner',
                label: 'Scanner',
                type: 'text',
                span: 4,
                block: 'equipment',
                subsectionLabel: EQUIPMENT_SUBSECTION_LABEL
            },
            {
                key: 'hasItero',
                label: 'Has iTero',
                type: 'select',
                span: 4,
                block: 'equipment',
                subsectionLabel: EQUIPMENT_SUBSECTION_LABEL
            }
        ]
    },
    {
        id: 'risk',
        label: 'Risk',
        fields: [
            {
                key: 'riskStatus',
                label: 'Risk status',
                type: 'select',
                kind: 'toneDropdown',
                toneOptions: RISK_STATUS_TONE_OPTIONS,
                span: 12,
                block: 'risk',
                optionLabels: PRACTICE_RISK_STATUS_LABELS
            },
            {
                key: 'riskNote',
                label: 'Reason',
                type: 'longtext',
                span: 12,
                block: 'risk',
                maxLength: PRACTICE_RISK_NOTE_MAX_LENGTH,
                placeholder: 'Why is this practice flagged?',
                helperText:
                    'Shown as a hover explanation everywhere this practice is listed - patients, work queue, conversations.',
                deemphasizeWhenFieldEquals: { field: 'riskStatus', value: 'normal' }
            }
        ]
    }
];

// Details shows the iTero connection box instead (its clinic id is the source of truth), so
// hasItero is only edited on create; it stays on the wire and on Monday.
const PRACTICE_DETAILS_HIDDEN_FIELD_KEYS = new Set<string>(['hasItero']);
// Half the Equipment row, so the iTero box (a subsection cell, also 6) sits beside Scanner.
const PRACTICE_DETAILS_SPANS: Readonly<Record<string, number>> = { scanner: 6 };

export const PRACTICE_DETAIL_GROUPS: PracticeFieldGroup[] = PRACTICE_FIELD_GROUPS.map((group) => ({
    ...group,
    fields: group.fields
        .filter((field) => !PRACTICE_DETAILS_HIDDEN_FIELD_KEYS.has(field.key))
        .map((field) => ({ ...field, span: PRACTICE_DETAILS_SPANS[field.key] ?? field.span }))
}));

export const PRACTICE_FIELD_DEFS: Record<string, PracticeFieldDef> = Object.fromEntries(
    PRACTICE_DETAIL_GROUPS.flatMap((group) => group.fields).map((field) => [field.key, field])
);

// Groups excluded from the practice CREATE flow - risk is set post-creation
// from the Details tab (the DB column defaults to 'normal'), so its group
// must never reach the create form or its POST body. See PracticeCreatePage
// and buildPracticeCreateBody below, both of which key off this.
const PRACTICE_CREATE_EXCLUDED_GROUP_IDS = new Set<PracticeFieldGroupId>(['risk']);

export const PRACTICE_CREATE_GROUPS: PracticeFieldGroup[] = PRACTICE_FIELD_GROUPS.filter(
    (group) => !PRACTICE_CREATE_EXCLUDED_GROUP_IDS.has(group.id)
);

const PRACTICE_CREATE_FIELD_DEFS: Record<string, PracticeFieldDef> = Object.fromEntries(
    PRACTICE_CREATE_GROUPS.flatMap((group) => group.fields).map((field) => [field.key, field])
);

// Hardcoded dropdown options, keyed by field name - the live values captured
// from the Monday board's configured column labels before
// GET /v2/practices/field-options was removed (Postgres is now the
// write-of-record and no longer round-trips through Monday's config for
// this). Two deliberate edits from the raw board data:
//   - daysNet: Monday's configured list had "3" twice - deduped to four
//     options. The values themselves (e.g. "30,60,90") are single opaque
//     strings, not lists - never split them.
//   - hasItero: Monday returned "Working on it" / "Done" / "Need info",
//     which are that platform's *default* labels for an unconfigured status
//     column, not real iTero values. Replaced with Yes/No - a reversible,
//     deliberate call, not a data-fidelity gap.
//
// state/country are a separate, later addition (the D2 redesign turned both
// into selects): hardcoded US state + Canadian province abbreviations and a
// two-country list, NOT sourced from Monday. Legacy free-text state values
// already saved on practices (e.g. "Texas") still render fine via
// withCurrentValueOption - see that helper - but only the abbreviated form is
// offered going forward. Country is stricter: its legacy variants ("USA",
// "CANADA") are display-normalized onto the two canonical options via
// normalizeFieldDisplayValue below, so the dropdown only ever shows United
// States and Canada.
export const PRACTICE_FIELD_OPTIONS: Record<string, string[]> = {
    // Raw wire values, in escalation order - see the riskStatus field's
    // optionLabels (PRACTICE_RISK_STATUS_LABELS) for what's actually shown.
    riskStatus: ['normal', 'warning', 'at_risk'],
    ach: ['Yes', 'No', 'Select'],
    // Deliberately narrowed to the three offered tiers - legacy values still
    // saved on practices (Vivos CSAM, the GPO variant, the Select
    // placeholder) render via withCurrentValueOption injecting the bound
    // value, but aren't offered for new selections.
    accountType: ['Concierge', 'Vivos', 'Adjunct'],
    version: ['V1', 'V2', 'Select'],
    daysNet: ['30,60,90', '10,40,70', '3', 'Select'],
    hasItero: ['Yes', 'No'],
    state: [
        'AL',
        'AK',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MD',
        'MA',
        'MI',
        'MN',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY',
        'AB',
        'BC',
        'MB',
        'NB',
        'NL',
        'NS',
        'ON',
        'PE',
        'QC',
        'SK'
    ],
    country: ['United States', 'Canada']
};

/**
 * The hardcoded option list for a select field - empty for every non-select
 * field. Does NOT include the record's current value if that value is
 * missing from the list; see withCurrentValueOption for that (rendering
 * only - kept separate so validateDirtyFields never needs to know which
 * value is "the original").
 */
export function resolveFieldOptions(field: PracticeFieldDef): string[] {
    return field.type === 'select' ? (PRACTICE_FIELD_OPTIONS[field.key] ?? []) : [];
}

/**
 * A select's live options must always include whatever value is currently
 * bound to it - real records hold values the hardcoded list doesn't cover
 * (33 practices have version = "Select"; hasItero may still hold Monday's
 * old default labels). Without this, a native <select> bound to a value with
 * no matching <option> renders with nothing selected, and saving anything
 * else on the record can silently blank that field out. Only ever adds the
 * *currently displayed* value (original or already-edited) - never mutates
 * the hardcoded list itself.
 */
// Legacy country spellings mapped onto the two canonical options. Displaying
// the canonical form means the select never needs a third injected option
// ("USA" x327 in production data); the underlying form value only changes to
// the canonical spelling when the user actually edits the field.
const COUNTRY_DISPLAY_ALIASES: Record<string, string> = {
    usa: 'United States',
    us: 'United States',
    'united states': 'United States',
    'united states of america': 'United States',
    canada: 'Canada',
    ca: 'Canada'
};

/**
 * Display-level canonicalization for select values whose legacy spellings
 * should collapse onto a hardcoded option instead of being injected as an
 * extra option (currently country only). Returns the value unchanged for
 * every other field.
 */
export function normalizeFieldDisplayValue(field: PracticeFieldDef, value: string): string {
    if (field.key !== 'country') return value;
    return COUNTRY_DISPLAY_ALIASES[value.trim().toLowerCase()] ?? value;
}

export function withCurrentValueOption(options: string[], currentValue: string): string[] {
    if (currentValue.trim() === '' || options.includes(currentValue)) return options;
    return [...options, currentValue];
}

/**
 * Enum/dropdown membership now lives entirely client-side: the backend no
 * longer validates a select field's value against Monday's live labels.
 * Returns a user-facing message when `value` isn't one of `options`, or null
 * when it's fine to submit.
 *
 * Only ever called for a *dirty* field (value !== the field's original
 * value - see usePracticeDetailsForm's dirtyKeys), against the raw hardcoded
 * list (not the current-value-augmented one the dropdown renders from).
 * That's what makes "a field is always valid unless the user changed it"
 * hold for free: an out-of-range original value never reaches this function
 * unless the user picked a different, necessarily-valid option - at which
 * point it's checked against the real list like any other edit.
 */
export function selectFieldError(
    field: PracticeFieldDef,
    value: string,
    options: string[]
): string | null {
    if (field.type !== 'select' || options.length === 0) return null;
    if (value.trim() === '') return null; // clearing a field is always valid
    return options.includes(value) ? null : "isn't one of the allowed options";
}

/**
 * NOT NULL fields (name/drFirstName/drLastName) can only be replaced, never
 * cleared - the server rejects a null there as a structural 400. Catching an
 * emptied required field here, before the request goes out, means the local
 * and server validation paths never disagree on what's savable.
 */
export function requiredFieldError(field: PracticeFieldDef, value: string): string | null {
    if (!field.required) return null;
    return value.trim() === '' ? "can't be empty" : null;
}

// Backstops a field's `maxLength` client-side, also for values that bypass an
// input's own maxLength attribute (e.g. pasted before it could clip them).
export function maxLengthFieldError(
    field: Pick<PracticeFieldDef, 'maxLength'>,
    value: string
): string | null {
    if (!field.maxLength) return null;
    return value.length > field.maxLength
        ? `can't be longer than ${field.maxLength} characters`
        : null;
}

export interface FieldSubsectionGroup {
    label: string;
    fields: PracticeFieldDef[];
}

/**
 * Buckets a card's fields into labelled subsections (Account's Classification
 * / Dental Monitoring / Billing / Manufacturer / Equipment), preserving the
 * fields array's own order. Fields without a subsectionLabel (Practice,
 * Doctor) are dropped, so callers treat an empty result as "no subsections -
 * render as one flat grid".
 */
export function groupFieldsBySubsection(fields: PracticeFieldDef[]): FieldSubsectionGroup[] {
    return fields.reduce<FieldSubsectionGroup[]>((groups, field) => {
        if (!field.subsectionLabel) return groups;
        const last = groups[groups.length - 1];
        if (last && last.label === field.subsectionLabel) {
            return [...groups.slice(0, -1), { ...last, fields: [...last.fields, field] }];
        }
        return [...groups, { label: field.subsectionLabel, fields: [field] }];
    }, []);
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

// Reads one field's raw wire value out of the (possibly block-nested) GET
// response - `practice` itself for a 'top' field, otherwise the
// corresponding block object (absent when that block wasn't `include`d).
function readFieldValue(practice: PracticeDetail, field: PracticeFieldDef): string | number | null {
    const container = practice as unknown as Record<string, unknown>;
    const source = field.block === 'top' ? container : container[field.block];
    if (!isRecord(source)) return null;
    const raw = source[field.key];
    return typeof raw === 'string' || typeof raw === 'number' ? raw : null;
}

function toDisplayValue(raw: string | number | null): string {
    return raw === null || raw === undefined ? '' : String(raw);
}

/**
 * Flattens the block-structured GET response into the form's flat internal
 * model (one display string per rendered field key) - the inverse of
 * buildPracticeDetailUpdate below. Only fields listed in PRACTICE_DETAIL_GROUPS
 * are included, so email2/network (still in the wire types, never rendered)
 * are naturally excluded here too.
 */
export function flattenPracticeDetail(practice: PracticeDetail): Record<string, string> {
    const values: Record<string, string> = {};
    for (const field of Object.values(PRACTICE_FIELD_DEFS)) {
        values[field.key] = toDisplayValue(readFieldValue(practice, field));
    }
    return values;
}

// Converts a field's edited display string back to what the API expects -
// numbers for the numeric fields, null for an emptied optional field. NOT
// NULL fields (name/drFirstName/drLastName) never reach here empty: they're
// caught by requiredFieldError() before the request is even built, so this
// can convert '' -> null unconditionally.
function toApiValue(field: PracticeFieldDef, value: string): string | number | null {
    const trimmed = value.trim();
    if (field.type === 'number') return trimmed === '' ? null : Number(trimmed);
    return trimmed === '' ? null : trimmed;
}

/**
 * Builds the nested PATCH body from a flat dirty-field set. Each block is
 * folded together immutably (spread, never mutated) as its fields are added
 * - a block only appears in the result at all if at least one of its fields
 * is dirty, matching the "only send what changed" contract. email2/network
 * can never appear here: they have no field definition, so they can never be
 * rendered, edited, or land in `dirtyKeys` in the first place.
 */
export function buildPracticeDetailUpdate(
    dirtyKeys: string[],
    edits: Record<string, string>
): PracticeDetailUpdate {
    const update: Record<string, unknown> = {};
    for (const key of dirtyKeys) {
        const field = PRACTICE_FIELD_DEFS[key];
        if (!field) continue;
        const value = toApiValue(field, edits[key]);
        if (field.block === 'top') {
            update[key] = value;
        } else {
            const existingBlock =
                (update[field.block] as Record<string, unknown> | undefined) ?? {};
            update[field.block] = { ...existingBlock, [key]: value };
        }
    }
    return update as PracticeDetailUpdate;
}

// Fields the create form requires - derived from the same `required` flag
// PRACTICE_CREATE_FIELD_DEFS already carries, so this can never drift from
// the fields actually marked required in PRACTICE_CREATE_GROUPS above.
export const PRACTICE_REQUIRED_FIELDS: PracticeFieldDef[] = Object.values(
    PRACTICE_CREATE_FIELD_DEFS
).filter((field) => field.required);

// A blank flat model - every field rendered on the create form mapped to ''
// - the create form's starting values before anything is typed.
export function emptyPracticeFieldValues(): Record<string, string> {
    const values: Record<string, string> = {};
    for (const field of Object.values(PRACTICE_CREATE_FIELD_DEFS)) {
        values[field.key] = '';
    }
    return values;
}

// Converts a field's typed display string to what a create POST expects -
// same numeric coercion as toApiValue, but an empty optional field is
// omitted entirely (undefined) rather than sent as null: the create
// contract only accepts non-empty fields/blocks, never a null placeholder.
function toCreateValue(field: PracticeFieldDef, value: string): string | number | undefined {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    return field.type === 'number' ? Number(trimmed) : trimmed;
}

/**
 * Builds the nested create-POST body from the form's full flat value set
 * (not a dirty subset - everything is new). Every empty optional field is
 * dropped, and a block only appears in the result if at least one of its
 * fields has a value, matching the "omit empty fields/blocks" create
 * contract. Required fields (name/drFirstName/drLastName) are validated
 * non-empty by the caller before this runs - see PRACTICE_REQUIRED_FIELDS
 * and requiredFieldError. Iterates PRACTICE_CREATE_FIELD_DEFS rather than
 * PRACTICE_FIELD_DEFS, so a create-excluded group (risk) can never land a
 * key in this body even if `values` happens to carry one.
 */
export function buildPracticeCreateBody(values: Record<string, string>): PracticeDetailUpdate {
    const body: Record<string, unknown> = {};
    for (const field of Object.values(PRACTICE_CREATE_FIELD_DEFS)) {
        const value = toCreateValue(field, values[field.key] ?? '');
        if (value === undefined) continue;
        if (field.block === 'top') {
            body[field.key] = value;
        } else {
            const existingBlock = (body[field.block] as Record<string, unknown> | undefined) ?? {};
            body[field.block] = { ...existingBlock, [field.key]: value };
        }
    }
    return body as PracticeDetailUpdate;
}
